import { handleError } from "@/utils/error";
import {
  ClientSearch,
  ClientSearchTriggerProps,
} from "@core/components/client-search";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { useDeleteNote } from "creso-core/hooks";
import { NoteDetail } from "creso-core/types";
import { format } from "date-fns";
import {
  CheckIcon,
  ChevronDown,
  SparklesIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { forwardRef, RefObject, useEffect, useRef, useState } from "react";
import { Button, cn, Textarea } from "web-design-system";
import { useOnClickOutside } from "usehooks-ts";
import { useNotebookContext } from "./notebook-context";

const NoteClientTrigger = forwardRef<
  HTMLButtonElement,
  ClientSearchTriggerProps
>(({ display, open, client, onClick, onClear }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className="text-xs w-60 flex flex-row gap-1 items-center"
  >
    {display}
    {client ? (
      <XIcon onClick={onClear} className="size-3" />
    ) : (
      <ChevronDown className={cn("size-3", open && "rotate-180")} />
    )}
  </button>
));
NoteClientTrigger.displayName = "NoteClientTrigger";

export function NoteCard(props: {
  note: NoteDetail;
  collapseEditing?: boolean;
}) {
  const { note, collapseEditing } = props;
  const { recordingStatus, reset, saveNote, setSelectedNote, clientIds } =
    useNotebookContext();
  const [isEditing, setIsEditing] = useState(note.id === -1);

  const [localNote, setLocalNote] = useState<NoteDetail>(note);

  useEffect(() => {
    setLocalNote(note);
    setIsEditing(note.id === -1);
  }, [note]);

  useEffect(() => {
    if (!collapseEditing) return;
    setLocalNote(note);
    setIsEditing(false);
  }, [collapseEditing, note]);

  const deleteNoteMutation = useDeleteNote();

  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useOnClickOutside(containerRef as RefObject<HTMLDivElement>, () => {
    setLocalNote(note);
    setIsEditing(false);
  });

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  function handleChange() {
    const el = textareaRef.current;
    if (!el) return;
    setLocalNote((n) => ({ ...n, summary: el.value }));
    resizeTextarea();
  }

  useEffect(() => {
    if (isEditing) resizeTextarea();
  }, [isEditing]);

  if (localNote.id === -1) {
    return (
      <>
        <motion.div
          key="editor"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col gap-4 p-4 bg-background rounded-lg focus-within:ring-1 ring-ring"
        >
          <AnimatePresence mode="wait" initial={false}>
            {(recordingStatus === "analysing" ||
              recordingStatus === "creating-todos") && (
              <motion.div
                key="analysing"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col gap-4"
              >
                <div className="flex flex-row gap-2 items-center">
                  <SparklesIcon className="size-4 text-tag-slate-foreground" />
                  <span className="font-semibold italic text-sm text-tag-slate-foreground">
                    Analyzing...
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <Textarea
            rows={10}
            value={localNote.summary}
            readOnly={recordingStatus !== "idle"}
            className="border-0 rounded-none p-0 ring-0! outline-0! read-only:text-muted-foreground read-only:italic"
            placeholder={`${recordingStatus !== "idle" ? "Dictate" : "Type"} a quick note and we will clean up, analyze and create tasks...`}
            autoFocus
            onChange={(e) => {
              const summary = e.target.value;
              setLocalNote((n) => ({ ...n, summary }));
              setSelectedNote((n) =>
                n && n.id === -1 ? { ...n, summary } : n
              );
            }}
          />
          <AnimatePresence>
            {recordingStatus === "creating-todos" && (
              <motion.div
                key="creating-todos"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="flex flex-col gap-3 overflow-hidden"
              >
                <div className="flex flex-row gap-2 items-center">
                  <SparklesIcon className="size-4 text-tag-slate-foreground" />
                  <span className="font-semibold text-sm text-tag-slate-foreground">
                    Creating to-dos
                  </span>
                </div>
                <div className="flex flex-col gap-3">
                  {[1, 1, 1].map((opacity, i) => (
                    <motion.div
                      key={i}
                      className="h-12 rounded-md bg-tag-slate"
                      initial={{ opacity }}
                      animate={
                        recordingStatus === "creating-todos"
                          ? { opacity: [opacity, opacity + 0.12, opacity] }
                          : { opacity }
                      }
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        delay: i * 0.15,
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        {recordingStatus === "recording" ||
        recordingStatus === "idle" ||
        recordingStatus === "paused" ? (
          <div className="flex flex-row mt-5 gap-2 items-center">
            <SparklesIcon className="size-4 text-tag-slate-foreground" />
            <span className="text-xs text-tag-slate-foreground">
              If you mention a client’s name, we’ll auto-assign the note to them
            </span>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="flex flex-col p-3 group">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col gap-2">
          {localNote.created_at ? (
            <span className="text-xs text-muted-foreground">
              {format(localNote.created_at, "PPP")}
            </span>
          ) : null}
          <span className="text-sm font-semibold">
            {localNote.title ?? "Untitled note"}
          </span>
        </div>
        <Button
          onClick={() =>
            deleteNoteMutation
              .mutateAsync(note.id)
              .then(() => {
                reset(undefined, true);
              })
              .catch((e) => handleError(e, "Failed to delete note"))
          }
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-100"
          variant={"ghost"}
          size="icon"
        >
          <Trash2Icon />
        </Button>
      </div>
      <div className="my-4 flex items-center gap-2">
        <p className="text-muted-foreground text-xs">For: </p>
        <ClientSearch
          options={{ modal: true }}
          client={
            localNote.client_id
              ? { id: localNote.client_id, name: localNote.client_name }
              : null
          }
          clientIds={clientIds}
          placeholder={clientIds ? "Select member" : "Self"}
          onSelect={(c) => {
            saveNote({ ...localNote, client_id: c?.id ?? null });
          }}
          trigger={NoteClientTrigger}
          showUnassign
        />
      </div>
      <ConditionalRenderer condition={!isEditing}>
        <div
          onClick={() => setIsEditing(true)}
          className="border hover:bg-card border-transparent rounded-lg -mx-4 p-4"
        >
          <span className="text-sm">{localNote.summary}</span>
        </div>
      </ConditionalRenderer>
      <ConditionalRenderer condition={isEditing}>
        <div
          ref={containerRef}
          className="border border-border rounded-lg flex -mx-4 gap-6 flex-col p-4"
        >
          <Textarea
            ref={textareaRef}
            autoFocus
            value={localNote.summary}
            readOnly={recordingStatus !== "idle"}
            className="min-h-0 read-only:text-muted-foreground p-0 border-0 text-sm ring-0! rounded-none outline-0! resize-none overflow-hidden"
            placeholder={`${recordingStatus !== "idle" ? "Dictate" : "Type"} a quick note and we will clean up, analyze and create tasks...`}
            onChange={handleChange}
          />
          <div className="justify-end flex flex-row gap-3 items-center">
            <Button
              onClick={() => {
                setLocalNote(note);
                setIsEditing(false);
              }}
              variant="outline"
              size="icon"
            >
              <XIcon />
            </Button>
            <Button
              onClick={() => saveNote(localNote)}
              variant="default"
              size="icon"
            >
              <CheckIcon />
            </Button>
          </div>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
